import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { autorun } from 'mobx';
import { compose } from 'recompose';
import usePostHogFeatureFlag from '../../hooks/usePostHogFeatureFlag';

import useComponentMounted from '../../hooks/useComponentMounted';
import { leadId, contractId } from '../../utils/onboarding';
import Product from '../../Model/Product';
import UserContract from '../../Model/UserContract';
import { ContractStatus, CheckoutExperience } from '../../CoachTools/utils/userContract';
import {
  HostedCheckoutType,
  HOSTED_CHECKOUT_FLAG_BY_TYPE,
  FeatureFlag,
  useChallengeCheckoutExperienceEnabled,
} from '../../utils/postHog';
import UserContractContext from './UserContractContext';

const UserContractContextProvider = ({
  children,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [contractDoc, setContractDoc] = useState(null);
  const [newTermsAvailable, setNewTermsAvailable] = useState(false);
  const [secondaryContractDoc, setSecondaryContractDoc] = useState(null);
  const [selectedContractDocId, setSelectedContractDocId] = useState(null);
  // undefined = still resolving the product; null = product not eligible.
  const [checkoutType, setCheckoutType] = useState(undefined);

  const prevContractRef = useRef(null);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const loadContract = async () => {
      let contractDocument;
      let contractCollection;

      if (contractId) {
        const document = new UserContract(contractId);
        await document.init();
        contractDocument = document;
      } else if (leadId) {
        contractCollection = await UserContract.getActivatableContractsForLead(leadId);
      }

      let documentToUse = null;
      // The autorun is used here to capture contract changes and update the context accordingly
      const disposer = autorun(() => {
        if (contractDocument && contractDocument.exists && contractDocument.status === ContractStatus.PENDING) {
          /*
            Reusable contracts are using the same status as the user contract. PENDING means the contract is not signed
            yet, so we can use it for generating USER contracts from this one.
          */
          documentToUse = contractDocument;
        } else if (contractCollection) {
          /*
            We are looking for the first contract that is not signed yet and is not a secondary contract.
            If there is no such contract, we are looking for the first contract that is in progress which is not a
            secondary contract.
          */
          documentToUse = contractCollection.docs.find((doc) => (
            doc.status === ContractStatus.PENDING && !doc.primaryContractId
          ))
            || contractCollection.docs.find((doc) => (
              doc.status === ContractStatus.IN_PROGRESS && !doc.primaryContractId
            ));
        }

        if (documentToUse && documentToUse.exists) {
          const contract = documentToUse;
          let secondaryContract = null;
          if (contract.secondaryContractId) {
            secondaryContract = contractCollection.docs.find((doc) => doc.id === contract.secondaryContractId);
          }

          if (isComponentMountedRef.current) {
            setContractDoc(contract);
            setSecondaryContractDoc(secondaryContract);

            if (contract && prevContractRef.current) {
              // If document was updated but status is still PENDING
              if (contract.updatedAt !== prevContractRef.current.updatedAt
                && contract.status === prevContractRef.current.status
                && contract.status === ContractStatus.PENDING) {
                setNewTermsAvailable(true);
              }
            }

            // Update previous state for next reaction - store a snapshot, not the object reference
            prevContractRef.current = {
              status: contract.status,
              updatedAt: contract.updatedAt,
            };
          }
        }
      });

      if (isComponentMountedRef) {
        setIsReady(true);
      }

      return disposer;
    };

    if (!isReady) {
      loadContract();
    }
  }, [
    isReady,
    isComponentMountedRef,
  ]);

  /*
    Resolve the contract's hosted-checkout type, or null if it isn't eligible. HYROX and
    CHALLENGE resolve on the product alone; AI_FRIEND additionally requires a monthly
    recurring contract; STANDARD_MONTHLY is the coach's per-contract opt-in and carries the
    strictest price rules. The server repeats this in hostedCheckoutEligibility.ts and has
    the final say — these branches must not accept anything it would reject.
  */
  useEffect(() => {
    if (!contractDoc?.productId) {
      setCheckoutType(null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const product = await Product.getById(contractDoc.productId);
      if (cancelled || !isComponentMountedRef.current) {
        return;
      }
      const { isAiFriendEnabled, isChallengeProduct, isHyroxProduct } = product || {};
      const {
        isMonthlyRecurringContract, isStandardMonthlyRecurringContract, isDiscountedFirstMonthContract,
        hasFutureStartDate, checkoutExperience,
      } = contractDoc || {};

      let type = null;
      if (isHyroxProduct) {
        type = HostedCheckoutType.HYROX;
      } else if (isChallengeProduct) {
        type = HostedCheckoutType.CHALLENGE;
      } else if (isAiFriendEnabled && isMonthlyRecurringContract) {
        type = HostedCheckoutType.AI_FRIEND;
      } else if (
        checkoutExperience === CheckoutExperience.STRIPE_HOSTED
        && isStandardMonthlyRecurringContract
        && !hasFutureStartDate
      ) {
        // Standard product whose contract is configured for Stripe Hosted Checkout.
        type = HostedCheckoutType.STANDARD_MONTHLY;
      } else if (
        checkoutExperience === CheckoutExperience.STRIPE_HOSTED
        && isDiscountedFirstMonthContract
        && !hasFutureStartDate
      ) {
        type = HostedCheckoutType.DISCOUNTED_FIRST_MONTH;
      }
      setCheckoutType(type);
    })();

    return () => {
      cancelled = true;
    };
  }, [contractDoc, isComponentMountedRef]);

  // Evaluate only the flag for the resolved checkout type (null key → disabled).
  const isCheckoutTypeFlagEnabled = usePostHogFeatureFlag(
    checkoutType ? HOSTED_CHECKOUT_FLAG_BY_TYPE[checkoutType] : null,
  );

  /*
    Hosted Stripe Checkout exists to collect payment, so a contract with
    nothing to charge never redirects there — it would present a card form for
    a $0 total. Free contracts always use the embedded NO_PAYMENT flow.
  */
  const isChallengeCheckoutExperienceEnabled = useChallengeCheckoutExperienceEnabled(contractDoc?.coach);

  /*
    A challenge stays on Stripe Hosted Checkout unless Avida-managed was picked for it and its coach is
    rolled out: challenges saved before the pick existed carry 'managed' as a default, not a choice.
  */
  const isAvidaManagedPickedChallenge = checkoutType === HostedCheckoutType.CHALLENGE
    && !!contractDoc?.isAvidaManagedCheckoutPicked;
  const isAvidaManagedChallenge = isAvidaManagedPickedChallenge && isChallengeCheckoutExperienceEnabled;
  /*
    The allowlist hook reads false, not undefined, while flags load. A picked challenge waits for the flag too,
    or it would redirect to Stripe Hosted Checkout before its coach's rollout is known.
  */
  const isChallengeCheckoutFlagEnabled = usePostHogFeatureFlag(
    isAvidaManagedPickedChallenge ? FeatureFlag.CHALLENGE_CHECKOUT_EXPERIENCE : null,
  );
  const isStripeCheckoutContract = !!checkoutType && isCheckoutTypeFlagEnabled === true && !contractDoc?.isFree
    && !isAvidaManagedChallenge;
  // Loading while the product type is still resolving, or a flag it depends on hasn't loaded.
  const isStripeHostedCheckoutLoading = checkoutType === undefined || isCheckoutTypeFlagEnabled === undefined
    || isChallengeCheckoutFlagEnabled === undefined;

  const context = useMemo(() => ({
    isReady,
    leadId,
    contractDoc,
    newTermsAvailable,
    secondaryContractDoc,
    selectedContractDocId,
    setSelectedContractDocId,
    selectedContractDoc: [contractDoc, secondaryContractDoc]
      .find((d) => d && d.id === selectedContractDocId) || contractDoc,
    isStripeCheckoutContract,
    isStripeHostedCheckoutLoading,
  }), [
    isReady,
    contractDoc,
    newTermsAvailable,
    secondaryContractDoc,
    selectedContractDocId,
    isStripeCheckoutContract,
    isStripeHostedCheckoutLoading,
  ]);

  return (
    <UserContractContext.Provider value={context}>
      {children}
    </UserContractContext.Provider>
  );
};

UserContractContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(UserContractContextProvider);
